import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronRight, 
  ChevronLeft, 
  Loader2, 
  CheckCircle2,
  AlertTriangle,
  Brain,
  User,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Likert scale options
const LIKERT_OPTIONS = [
  { value: "1", label: "Strongly Disagree" },
  { value: "2", label: "Disagree" },
  { value: "3", label: "Neutral" },
  { value: "4", label: "Agree" },
  { value: "5", label: "Strongly Agree" },
];

// Leader Dark Triad items
const LEADER_ITEMS = [
  { id: "leadermach1", text: "This person tends to manipulate others to get their way.", trait: "Machiavellianism" },
  { id: "leadermach2", text: "This person has used deceit or lied to get their way.", trait: "Machiavellianism" },
  { id: "leadermach3", text: "This person tends to exploit others towards their own end.", trait: "Machiavellianism" },
  { id: "leadermach4", text: "This person has used flattery to get their way.", trait: "Machiavellianism" },
  { id: "leaderpsycho1", text: "This person tends to lack remorse.", trait: "Psychopathy" },
  { id: "leaderpsycho2", text: "This person tends to be callous or insensitive.", trait: "Psychopathy" },
  { id: "leaderpsycho3", text: "This person tends to be unconcerned with the morality of their actions.", trait: "Psychopathy" },
  { id: "leaderpsycho4", text: "This person tends to be cynical.", trait: "Psychopathy" },
  { id: "leadernarc1", text: "This person tends to want others to admire them.", trait: "Narcissism" },
  { id: "leadernarc2", text: "This person tends to want others to pay attention to them.", trait: "Narcissism" },
  { id: "leadernarc3", text: "This person tends to seek prestige or status.", trait: "Narcissism" },
  { id: "leadernarc4", text: "This person tends to expect special favors from others.", trait: "Narcissism" },
];

// Participant Big Five items (R = reverse scored)
const PARTICIPANT_ITEMS = [
  { id: "extra1", text: "I am the life of the party.", trait: "Extraversion", reverse: false },
  { id: "agree1", text: "I sympathize with others' feelings.", trait: "Agreeableness", reverse: false },
  { id: "conscient1", text: "I get chores done right away.", trait: "Conscientiousness", reverse: false },
  { id: "neurot1", text: "I have frequent mood swings.", trait: "Neuroticism", reverse: false },
  { id: "open1", text: "I have a vivid imagination.", trait: "Openness", reverse: false },
  { id: "extra2R", text: "I don't talk a lot.", trait: "Extraversion", reverse: true },
  { id: "agree2R", text: "I am not interested in other people's problems.", trait: "Agreeableness", reverse: true },
  { id: "conscient2R", text: "I often forget to put things back in their proper place.", trait: "Conscientiousness", reverse: true },
  { id: "neurot2R", text: "I am relaxed most of the time.", trait: "Neuroticism", reverse: true },
  { id: "open2R", text: "I am not interested in abstract ideas.", trait: "Openness", reverse: true },
  { id: "extra3", text: "I talk to a lot of different people at parties.", trait: "Extraversion", reverse: false },
  { id: "agree3", text: "I feel others' emotions.", trait: "Agreeableness", reverse: false },
  { id: "conscient3", text: "I like order.", trait: "Conscientiousness", reverse: false },
  { id: "neurot3", text: "I get upset easily.", trait: "Neuroticism", reverse: false },
  { id: "open3R", text: "I have difficulty understanding abstract ideas.", trait: "Openness", reverse: true },
  { id: "extra4R", text: "I keep in the background.", trait: "Extraversion", reverse: true },
  { id: "agree4R", text: "I am not really interested in others.", trait: "Agreeableness", reverse: true },
  { id: "conscient4R", text: "I make a mess of things.", trait: "Conscientiousness", reverse: true },
  { id: "neurot4R", text: "I seldom feel blue.", trait: "Neuroticism", reverse: true },
  { id: "open4R", text: "I do not have a good imagination.", trait: "Openness", reverse: true },
];

const STEPS = [
  { id: 1, name: "Leader Behavior", icon: AlertTriangle },
  { id: 2, name: "Leader Assessment", icon: Brain },
  { id: 3, name: "Your Coping", icon: FileText },
  { id: 4, name: "Self Assessment", icon: User },
  { id: 5, name: "Demographics", icon: User },
];

const IntakeForm = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    leaderBehavior: "",
    leaderRatings: {},
    copingStrategies: "",
    leaderGender: "",
    participantRatings: {},
    demographics: {
      age: "",
      gender: "",
      occupation: "",
      country: "",
      incomeBracket: "",
    },
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateLeaderRating = (itemId, value) => {
    setFormData(prev => ({
      ...prev,
      leaderRatings: { ...prev.leaderRatings, [itemId]: parseInt(value) }
    }));
  };

  const updateParticipantRating = (itemId, value) => {
    setFormData(prev => ({
      ...prev,
      participantRatings: { ...prev.participantRatings, [itemId]: parseInt(value) }
    }));
  };

  const updateDemographic = (key, value) => {
    setFormData(prev => ({
      ...prev,
      demographics: { ...prev.demographics, [key]: value }
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.leaderBehavior.trim().length >= 50;
      case 2:
        return Object.keys(formData.leaderRatings).length === LEADER_ITEMS.length;
      case 3:
        return formData.copingStrategies.trim().length >= 20 && formData.leaderGender !== "";
      case 4:
        return Object.keys(formData.participantRatings).length === PARTICIPANT_ITEMS.length;
      case 5:
        const d = formData.demographics;
        return d.age && d.gender && d.occupation && d.country;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!bookingId) {
      toast.error("Invalid booking reference");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/intake-form`, {
        booking_id: bookingId,
        ...formData,
      });
      
      setResults(response.data.scores);
      setSubmitted(true);
      toast.success("Form submitted successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.detail || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 5) {
      handleSubmit();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = (currentStep / STEPS.length) * 100;

  // Results visualization component
  const ScoreBar = ({ label, score, maxScore = 5, color = "#D4AF37" }) => {
    const percentage = (score / maxScore) * 100;
    return (
      <div className="mb-4">
        <div className="flex justify-between mb-1">
          <span className="text-slate-300 text-sm">{label}</span>
          <span className="text-white font-mono text-sm">{score.toFixed(2)}</span>
        </div>
        <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  if (!bookingId) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-4">Invalid Link</h1>
          <p className="text-slate-400 mb-6">
            This form requires a valid booking reference. Please use the link from your confirmation email.
          </p>
          <Link to="/">
            <Button className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted && results) {
    return (
      <div className="min-h-screen bg-[#020617] py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-white mb-2">Thank You!</h1>
            <p className="text-slate-400">
              Your pre-session assessment has been submitted. Here are your results:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Leader Dark Triad Scores */}
            <div className="bg-slate-900/50 border border-slate-800 p-6">
              <h2 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#D4AF37]" />
                Leader's Dark Triad Profile
              </h2>
              <ScoreBar label="Machiavellianism" score={results.leader.machiavellianism} color="#EF4444" />
              <ScoreBar label="Psychopathy" score={results.leader.psychopathy} color="#8B5CF6" />
              <ScoreBar label="Narcissism" score={results.leader.narcissism} color="#F59E0B" />
              <div className="mt-6 pt-4 border-t border-slate-700">
                <div className="flex justify-between">
                  <span className="text-slate-400 text-sm">Overall Dark Triad</span>
                  <span className="text-[#D4AF37] font-mono font-medium">{results.leader.overall.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Participant Big Five Scores */}
            <div className="bg-slate-900/50 border border-slate-800 p-6">
              <h2 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-[#38BDF8]" />
                Your Big Five Personality Profile
              </h2>
              <ScoreBar label="Extraversion" score={results.participant.extraversion} color="#38BDF8" />
              <ScoreBar label="Agreeableness" score={results.participant.agreeableness} color="#10B981" />
              <ScoreBar label="Conscientiousness" score={results.participant.conscientiousness} color="#F59E0B" />
              <ScoreBar label="Neuroticism" score={results.participant.neuroticism} color="#EF4444" />
              <ScoreBar label="Openness" score={results.participant.openness} color="#8B5CF6" />
            </div>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 p-6 mt-8">
            <h3 className="font-serif text-lg text-white mb-3">What's Next?</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              A copy of these results has been emailed to you and the clinic. During your session, 
              your behavioural scientist will use this information to provide personalized, evidence-based strategies 
              tailored to your specific situation and personality profile.
            </p>
          </div>

          <div className="text-center mt-8">
            <Link to="/">
              <Button className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-8 py-6 uppercase tracking-widest text-sm">
                Return to Homepage
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] py-12 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-serif text-2xl md:text-3xl text-white mb-2">
            Pre-Session <span className="text-[#D4AF37]">Assessment</span>
          </h1>
          <p className="text-slate-400 text-sm">
            Complete this form before your session with the Bad Boss Clinic
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((step) => (
              <div 
                key={step.id} 
                className={`flex flex-col items-center ${
                  currentStep >= step.id ? 'text-[#D4AF37]' : 'text-slate-600'
                }`}
              >
                <step.icon className="w-5 h-5 mb-1" />
                <span className="text-xs hidden sm:block">{step.name}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2 bg-slate-800" />
        </div>

        {/* Form Content */}
        <div className="bg-slate-900/50 border border-slate-800 p-6 md:p-8">
          {/* Step 1: Leader Behavior */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fadeInUp">
              <div>
                <h2 className="font-serif text-xl text-white mb-2">Describe Your Leader's Behavior</h2>
                <p className="text-slate-400 text-sm">
                  Please describe what your leader does that makes your life difficult. 
                  Be as specific and detailed as possible.
                </p>
              </div>
              <div>
                <Textarea
                  data-testid="leader-behavior-input"
                  value={formData.leaderBehavior}
                  onChange={(e) => updateFormData("leaderBehavior", e.target.value)}
                  placeholder="Describe specific behaviors, situations, and their impact on you..."
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 min-h-[200px] rounded-none"
                />
                <p className="text-slate-500 text-xs mt-2">
                  {formData.leaderBehavior.length} characters (minimum 50 required)
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Leader Assessment (Dark Triad) */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fadeInUp">
              <div>
                <h2 className="font-serif text-xl text-white mb-2">Rate Your Leader</h2>
                <p className="text-slate-400 text-sm">
                  Please rate how much you agree with each statement about your leader.
                </p>
              </div>
              <div className="space-y-6">
                {LEADER_ITEMS.map((item, idx) => (
                  <div key={item.id} className="border-b border-slate-800 pb-4">
                    <p className="text-white text-sm mb-3">
                      <span className="text-slate-500 mr-2">{idx + 1}.</span>
                      {item.text}
                    </p>
                    <RadioGroup
                      value={formData.leaderRatings[item.id]?.toString() || ""}
                      onValueChange={(value) => updateLeaderRating(item.id, value)}
                      className="flex flex-wrap gap-2"
                    >
                      {LIKERT_OPTIONS.map((option) => (
                        <div key={option.value} className="flex items-center">
                          <RadioGroupItem
                            value={option.value}
                            id={`${item.id}-${option.value}`}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={`${item.id}-${option.value}`}
                            className="px-3 py-2 text-xs border border-slate-700 cursor-pointer hover:border-[#D4AF37] peer-data-[state=checked]:bg-[#D4AF37] peer-data-[state=checked]:text-slate-950 peer-data-[state=checked]:border-[#D4AF37] transition-all"
                          >
                            {option.value}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <div className="flex justify-between text-xs text-slate-600 mt-1 px-1">
                      <span>Strongly Disagree</span>
                      <span>Strongly Agree</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-xs">
                {Object.keys(formData.leaderRatings).length} of {LEADER_ITEMS.length} items rated
              </p>
            </div>
          )}

          {/* Step 3: Coping Strategies */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fadeInUp">
              <div>
                <h2 className="font-serif text-xl text-white mb-2">Your Coping Strategies</h2>
                <p className="text-slate-400 text-sm">
                  Describe what you've done to cope with this situation.
                </p>
              </div>
              <div>
                <Textarea
                  data-testid="coping-strategies-input"
                  value={formData.copingStrategies}
                  onChange={(e) => updateFormData("copingStrategies", e.target.value)}
                  placeholder="Describe any behavioral changes or strategies that were successful or unsuccessful..."
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 min-h-[150px] rounded-none"
                />
                <p className="text-slate-500 text-xs mt-2">
                  {formData.copingStrategies.length} characters (minimum 20 required)
                </p>
              </div>
              <div>
                <Label className="text-slate-300 mb-3 block">Leader's Gender</Label>
                <RadioGroup
                  value={formData.leaderGender}
                  onValueChange={(value) => updateFormData("leaderGender", value)}
                  className="flex gap-4"
                >
                  {["Male", "Female", "Other"].map((gender) => (
                    <div key={gender} className="flex items-center">
                      <RadioGroupItem
                        value={gender.toLowerCase()}
                        id={`gender-${gender}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`gender-${gender}`}
                        className="px-4 py-2 text-sm border border-slate-700 cursor-pointer hover:border-[#D4AF37] peer-data-[state=checked]:bg-[#D4AF37] peer-data-[state=checked]:text-slate-950 peer-data-[state=checked]:border-[#D4AF37] transition-all"
                      >
                        {gender}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {/* Step 4: Self Assessment (Big Five) */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-fadeInUp">
              <div>
                <h2 className="font-serif text-xl text-white mb-2">About Yourself</h2>
                <p className="text-slate-400 text-sm">
                  Please rate how much you agree with each statement about yourself.
                </p>
              </div>
              <div className="space-y-6">
                {PARTICIPANT_ITEMS.map((item, idx) => (
                  <div key={item.id} className="border-b border-slate-800 pb-4">
                    <p className="text-white text-sm mb-3">
                      <span className="text-slate-500 mr-2">{idx + 1}.</span>
                      {item.text}
                    </p>
                    <RadioGroup
                      value={formData.participantRatings[item.id]?.toString() || ""}
                      onValueChange={(value) => updateParticipantRating(item.id, value)}
                      className="flex flex-wrap gap-2"
                    >
                      {LIKERT_OPTIONS.map((option) => (
                        <div key={option.value} className="flex items-center">
                          <RadioGroupItem
                            value={option.value}
                            id={`${item.id}-${option.value}`}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={`${item.id}-${option.value}`}
                            className="px-3 py-2 text-xs border border-slate-700 cursor-pointer hover:border-[#38BDF8] peer-data-[state=checked]:bg-[#38BDF8] peer-data-[state=checked]:text-slate-950 peer-data-[state=checked]:border-[#38BDF8] transition-all"
                          >
                            {option.value}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    <div className="flex justify-between text-xs text-slate-600 mt-1 px-1">
                      <span>Strongly Disagree</span>
                      <span>Strongly Agree</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-xs">
                {Object.keys(formData.participantRatings).length} of {PARTICIPANT_ITEMS.length} items rated
              </p>
            </div>
          )}

          {/* Step 5: Demographics */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-fadeInUp">
              <div>
                <h2 className="font-serif text-xl text-white mb-2">Demographics</h2>
                <p className="text-slate-400 text-sm">
                  Please provide some basic information about yourself.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Age</Label>
                  <Input
                    type="number"
                    data-testid="age-input"
                    value={formData.demographics.age}
                    onChange={(e) => updateDemographic("age", e.target.value)}
                    placeholder="Your age"
                    className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Gender</Label>
                  <select
                    data-testid="gender-select"
                    value={formData.demographics.gender}
                    onChange={(e) => updateDemographic("gender", e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
                <div>
                  <Label className="text-slate-300">Occupation</Label>
                  <Input
                    data-testid="occupation-input"
                    value={formData.demographics.occupation}
                    onChange={(e) => updateDemographic("occupation", e.target.value)}
                    placeholder="Your occupation"
                    className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Country</Label>
                  <Input
                    data-testid="country-input"
                    value={formData.demographics.country}
                    onChange={(e) => updateDemographic("country", e.target.value)}
                    placeholder="Your country"
                    className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-slate-300">Income Bracket (Optional)</Label>
                  <select
                    data-testid="income-select"
                    value={formData.demographics.incomeBracket}
                    onChange={(e) => updateDemographic("incomeBracket", e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="under-30k">Under €30,000</option>
                    <option value="30k-50k">€30,000 - €50,000</option>
                    <option value="50k-75k">€50,000 - €75,000</option>
                    <option value="75k-100k">€75,000 - €100,000</option>
                    <option value="100k-150k">€100,000 - €150,000</option>
                    <option value="over-150k">Over €150,000</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <Button
              data-testid="next-step-btn"
              onClick={nextStep}
              disabled={!canProceed() || loading}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-6 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  {currentStep === 5 ? "Submit" : "Continue"}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeForm;
